// This file was auto-generated by Fern from our API Definition.
import { normalizeClientOptionsWithAuth } from "../../../../BaseClient.js";
import { mergeHeaders } from "../../../../core/headers.js";
import * as core from "../../../../core/index.js";
import * as environments from "../../../../environments.js";
import { handleNonStatusCodeError } from "../../../../errors/handleNonStatusCodeError.js";
import * as errors from "../../../../errors/index.js";
import * as AgentaApi from "../../../index.js";
/**
 * Authentication discovery, organization access checks, and SSO callback endpoints.
 */
export class AccessClient {
    _options;
    constructor(options) {
        this._options = normalizeClientOptionsWithAuth(options);
    }
    /**
     * Return the effective plan catalog: slug -> entitlement controls.
     *
     * The shape mirrors what `AGENTA_ACCESS_PLANS` accepts, but fully parsed
     * and validated. The frontend reads `flags`, `counters`, `gauges`, and
     * `throttles` from here rather than slug-matching against constants.
     *
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.access.fetchAccessPlans()
     */
    fetchAccessPlans(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchAccessPlans(requestOptions));
    }
    async __fetchAccessPlans(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "access/plans"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/access/plans");
    }
    /**
     * Discover authentication methods available for a given email.
     *
     * This endpoint does NOT reveal:
     * - Organization names
     * - User existence (optionally - currently does for UX)
     * - Detailed policy information
     *
     * Returns minimal information needed for authentication flow.
     *
     * @param {AgentaApi.DiscoverRequest} request
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.access.discoverAccess({
     *         email: "email"
     *     })
     */
    discoverAccess(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__discoverAccess(request, requestOptions));
    }
    async __discoverAccess(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "auth/discover"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/auth/discover");
    }
    /**
     * Check if the current session satisfies the organization's auth policy.
     *
     * Returns 200 when access is allowed, 403 with AUTH_UPGRADE_REQUIRED when not.
     *
     * @param {AgentaApi.CheckOrganizationAccessRequest} request
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.access.checkOrganizationAccess({
     *         organization_id: "organization_id"
     *     })
     */
    checkOrganizationAccess(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__checkOrganizationAccess(request, requestOptions));
    }
    async __checkOrganizationAccess(request, requestOptions) {
        const { organization_id: organizationId } = request;
        const _queryParams = {
            organization_id: organizationId,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "auth/access"),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/auth/access");
    }
    /**
     * @param {AgentaApi.SessionIdentitiesUpdate} request
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.access.updateSessionIdentities({
     *         session_identities: ["session_identities"]
     *     })
     */
    updateSessionIdentities(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__updateSessionIdentities(request, requestOptions));
    }
    async __updateSessionIdentities(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "auth/session/identities"),
            method: "PATCH",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PATCH", "/auth/session/identities");
    }
    /**
     * Custom SSO callback endpoint that redirects to SuperTokens.
     *
     * This endpoint:
     * 1. Accepts clean URL path: /auth/sso/callback/{organization_slug}/{provider_slug}
     * 2. Validates the organization and provider exist
     * 3. Builds SuperTokens thirdPartyId: sso:{organization_slug}:{provider_slug}
     * 4. Redirects to SuperTokens callback: /auth/callback/{thirdPartyId}
     *
     * SuperTokens then handles:
     * 1. Exchange code for tokens (using our dynamic provider config)
     * 2. Get user info
     * 3. Call our sign_in_up override (creates user_identity, adds user_identities to session)
     * 4. Redirect to frontend with session cookie
     *
     * @param {AgentaApi.SsoCallbackRedirectRequest} request
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.access.ssoCallbackRedirect({
     *         organization_slug: "organization_slug",
     *         provider_slug: "provider_slug"
     *     })
     */
    ssoCallbackRedirect(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__ssoCallbackRedirect(request, requestOptions));
    }
    async __ssoCallbackRedirect(request, requestOptions) {
        const { organization_slug: organizationSlug, provider_slug: providerSlug } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `auth/sso/callback/${core.url.encodePathParam(organizationSlug)}/${core.url.encodePathParam(providerSlug)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/auth/sso/callback/{organization_slug}/{provider_slug}");
    }
    /**
     * Evaluate a permission against the AUTHENTICATED scope.
     *
     * `scope_id` asserts that the request was authenticated for that scope; it never
     * selects it. A caller asking about a specific project must also send the
     * `project_id` query param the auth middleware reads — a session with no explicit
     * `project_id` is scoped to the default project, and any other `scope_id` is denied
     * regardless of the caller's role there.
     *
     * @param {AgentaApi.CheckPermissionsRequest} request
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.access.checkPermissions()
     */
    checkPermissions(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__checkPermissions(request, requestOptions));
    }
    async __checkPermissions(request = {}, requestOptions) {
        const { action, scope_type: scopeType, scope_id: scopeId, resource_type: resourceType, resource_id: resourceId, without_grants: withoutGrants, } = request;
        const _queryParams = {
            action,
            scope_type: scopeType,
            scope_id: scopeId,
            resource_type: resourceType,
            resource_id: resourceId,
            without_grants: withoutGrants,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "access/permissions/check"),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/access/permissions/check");
    }
    /**
     * Return the effective role catalog per scope (organization,
     * workspace, project). RBAC is an OSS feature, so this is served in both
     * editions; the frontend reads the `workspace` scope for the members UI.
     *
     * @param {AccessClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.access.fetchAccessRoles()
     */
    fetchAccessRoles(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchAccessRoles(requestOptions));
    }
    async __fetchAccessRoles(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "access/roles"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/access/roles");
    }
}
